import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Scale } from "lucide-react";
import { API_BASE } from "@/constants/form";
import { setToken, formatApiError } from "@/admin/api";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/login`, {
        username,
        password,
      });
      setToken(data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#FBF9F5] flex items-center justify-center px-5"
    >
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm p-8 shadow-[0_12px_40px_rgba(26,23,21,0.06)]"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center border border-[#1A1715] rounded-sm">
            <Scale className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div>
            <h1 className="font-serif font-semibold text-xl text-[#1A1715]">
              Campaign Admin
            </h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]">
              Authorized access only
            </p>
          </div>
        </div>

        {error && (
          <p
            data-testid="admin-login-error"
            role="alert"
            className="mt-6 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-3.5 py-2.5 font-sans text-sm font-medium text-[#B91C1C]"
          >
            {error}
          </p>
        )}

        <div className="mt-7">
          <label
            htmlFor="admin-username"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#44403C] font-semibold"
          >
            Username
          </label>
          <input
            id="admin-username"
            data-testid="admin-username-input"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-2 w-full bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-4 py-3 font-sans text-sm text-[#1A1715] focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]"
          />
        </div>
        <div className="mt-5">
          <label
            htmlFor="admin-password"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#44403C] font-semibold"
          >
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-4 py-3 font-sans text-sm text-[#1A1715] focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]"
          />
        </div>
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="mt-7 w-full bg-[#1A1715] text-[#FBF9F5] font-sans font-semibold text-sm px-6 py-3.5 rounded-sm transition-colors duration-300 hover:bg-[#C2410C] disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </main>
  );
}
